import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  DashboardFilters,
  DashboardMetrics,
  PayMetrics,
} from '../types/dashboard.types';

// ============================================================================
// TYPES
// ============================================================================

export interface DashboardState {
  // Data
  metrics: DashboardMetrics | null;
  payMetrics: PayMetrics | null;
  filters: DashboardFilters;

  // UI State
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;

  // Actions
  setFilters: (filters: Partial<DashboardFilters>) => void;
  fetchDashboardMetrics: () => Promise<void>;
  fetchPayMetrics: () => Promise<void>;
  refreshDashboard: () => Promise<void>;
  clearDashboard: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// ============================================================================
// STORE
// ============================================================================

export const useDashboardStore = create<DashboardState>()(
  devtools(
    persist(
      (set, get) => ({
        // ========================================================================
        // INITIAL STATE
        // ========================================================================
        metrics: null,
        payMetrics: null,
        filters: {
          dateRange: 'monthly',
          startDate: undefined,
          endDate: undefined,
        },
        isLoading: false,
        error: null,
        lastUpdated: null,

        // ========================================================================
        // ACTIONS
        // ========================================================================

        setFilters: (filters) => {
          const currentFilters = get().filters;
          set({
            filters: { ...currentFilters, ...filters },
          });

          // Auto-refresh when filters change
          get().fetchDashboardMetrics();
          get().fetchPayMetrics();
        },

        fetchDashboardMetrics: async () => {
          set({ isLoading: true, error: null });

          try {
            const { DashboardService } = await import('../services/dashboard.service');
            const response = await DashboardService.getDashboardMetrics(get().filters);

            set({
              metrics: response.data,
              lastUpdated: Date.now(),
              isLoading: false,
            });
          } catch (error: any) {
            console.error('Failed to fetch dashboard metrics:', error);
            set({
              error: error.message || 'Failed to fetch dashboard metrics',
              isLoading: false,
            });
          }
        },

        fetchPayMetrics: async () => {
          try {
            const { DashboardService } = await import('../services/dashboard.service');
            const response = await DashboardService.getPayMetrics(get().filters);

            set({
              payMetrics: response.data,
              lastUpdated: Date.now(),
            });
          } catch (error: any) {
            console.error('Failed to fetch pay metrics:', error);
            // Don't set error for pay metrics to avoid blocking main dashboard
          }
        },

        refreshDashboard: async () => {
          await Promise.all([get().fetchDashboardMetrics(), get().fetchPayMetrics()]);
        },

        clearDashboard: () => {
          set({
            metrics: null,
            payMetrics: null,
            error: null,
            lastUpdated: null,
          });
        },

        setLoading: (isLoading) => {
          set({ isLoading });
        },

        setError: (error) => {
          console.error('Dashboard Error:', error);
          set({ error, isLoading: false });
        },
      }),
      {
        name: 'dashboard-storage',
        partialize: (state) => ({
          filters: state.filters,
          lastUpdated: state.lastUpdated,
        }),
      }
    ),
    { name: 'DashboardStore' }
  )
);
